import Router from "next/router";
import { create } from "zustand";
import localforage from "localforage";
import { toast } from "~served/ui-comps/toast";
import {
	TILL_MODE,
	CALENDAR_VIEW,
	DEFAULT_GROUP_NAME,
	LOWEST_DENOMINATOR,
	PREFERRED_LOCATION_VIEW,
} from "~/configs/constants";
import { createCompositionBaseline } from "~/utils/till-baseline";
import { getTillHydrationKey } from "~/utils/till-hydration-key";
import { STORAGE_KEYS } from "~/libs/storage-keys";
import { queryClient } from "~/providers/query-provider";
import { getAuthQueryKey } from "~/queries/use-auth-cache";
import { getCurrentVenueCache } from "~/queries/use-get-current-venue";
import { getOperationalLocationsCache } from "~/queries/use-get-operational-locations";
import {
	GetCurrentVenueQuery,
	Order,
	OrderItem,
	UpdateOrderItemInput,
} from "~/types/__generated/gql/graphql";
import { Actions, State } from "~/types/store";
import { createSelectors } from "~/types/utils";
import { DEFAULT_PAGINATION_LIMIT } from "~/utils/pagination";
import {
	applyServiceChargeBdToOrder,
	applyServiceChargeBdToOrderGroup,
	applyServiceChargeToOrder,
	applyServiceChargeToOrderGroup,
	applyTaxToOrder,
	applyTaxToOrderGroup,
	applyVatToOrder,
	applyVatToOrderGroup,
} from "~served/utils/calculation";
import { dayjs } from "~served/utils/libs/dayjs";
import {
	getNextCourseIndex,
	mergeCourseIntoPrevious,
} from "~served/utils/print/utils";

let tillSessionCount = 0;
const nextTillSessionId = () => ++tillSessionCount;

const INITIAL_STATE: State = {
	isReady: false,
	isConnectedToServer: true,
	isFeatureLockedDialogOpen: false,
	isUpdateAvailable: false,
	isUpdateInstalled: true,
	token: null,
	refreshToken: null,
	venueId: null,
	staffToken: null,

	denominator: LOWEST_DENOMINATOR,
	preferredLocation: DEFAULT_GROUP_NAME,
	preferredLocationView: PREFERRED_LOCATION_VIEW.LIST,
	preferredCalendarView: CALENDAR_VIEW.MONTH,
	preferredLocationOnTill: null,
	roomMasterError: null,
	lastOfflineDataDownloadAt: null,
	isDownloadingOfflineData: false,

	reports: {
		summary: {
			from: dayjs().startOf("d").toISOString(),
			to: dayjs().endOf("D").millisecond(999).toISOString(),
		},
		customs: {
			from: dayjs().startOf("d").toISOString(),
			to: dayjs().endOf("D").millisecond(999).toISOString(),
		},
		receipts: {
			from: dayjs().startOf("d").toISOString(),
			to: dayjs().endOf("D").millisecond(999).toISOString(),
		},
		categories: {
			from: dayjs().startOf("d").toISOString(),
			to: dayjs().endOf("D").millisecond(999).toISOString(),
		},
		items: {
			from: dayjs().startOf("d").toISOString(),
			to: dayjs().endOf("D").millisecond(999).toISOString(),
		},
		options: {
			from: dayjs().startOf("d").toISOString(),
			to: dayjs().endOf("D").millisecond(999).toISOString(),
		},
		staffs: {
			from: dayjs().startOf("d").toISOString(),
			to: dayjs().endOf("D").millisecond(999).toISOString(),
		},
		locations: {
			from: dayjs().startOf("d").toISOString(),
			to: dayjs().endOf("D").millisecond(999).toISOString(),
		},
		details: {
			from: dayjs().startOf("d").toISOString(),
			to: dayjs().endOf("D").millisecond(999).toISOString(),
		},
		voids: {
			from: dayjs().startOf("d").toISOString(),
			to: dayjs().endOf("D").millisecond(999).toISOString(),
		},
	},

	till: {
		items: [],
		cancelled_items: [],
		note: undefined,
		customer: undefined,
		location: "",
		alertContext: {},
		orderEditContext: undefined,

		payment_types: [],
		cash_received: [],
		cash_returned: [],
		isInternalReceipt: false,
		tips: [],
		current_course_index: 0,
		room_master_reference: null,
		room_master_folio: null,
		room_number: "",
		prevItems: [],
		orders: [],
		isLoading: false,
		isPaymentsPanelOpen: false,
		selectedCategory: null,
		selectedItemTags: [],
		selectedItem: null,
		selectedCartItem: null,
		isCopyingItem: false,
		walletTransactionInput: null,
		activeMasterView: null as {
			masterId: string;
			masterTitle: string;
			masterItemRef: string;
			masterItemData: OrderItem | null;
		} | null,
		masterItemFilterByCategory: {},
		cloudbeds_payment: null,
		addonsToggles: {
			vat: false,
			serviceCharge: false,
			serviceChargeBd: false,
			tax: false,
			cardSurcharge: false,
		},
	},

	ordersLog: {
		filters: {
			from: 0,
			size: DEFAULT_PAGINATION_LIMIT,
			date: null,
			indices: null,
		},
	},

	customers: {
		filters: {
			from: 0,
			size: DEFAULT_PAGINATION_LIMIT,
			query: "",
		},
	},

	printLogs: {
		filters: {
			from: 0,
			size: DEFAULT_PAGINATION_LIMIT,
			date: null,
			orderIndices: null,
			receiptNumbers: null,
		},
	},

	rawMaterialReceived: {
		filters: {
			page: 0,
			size: DEFAULT_PAGINATION_LIMIT,
		},
	},
};

const resolveInitialAddonToggles = ({
	venue,
	locationNoVat,
	locationNoServiceCharge,
}: {
	venue: NonNullable<GqlResultOf<GetCurrentVenueQuery>>;
	locationNoVat: boolean;
	locationNoServiceCharge: boolean;
}) => {
	const noSc = locationNoServiceCharge || !!venue.is_service_charge_buried;
	const noScBd = locationNoServiceCharge || !!venue.is_service_charge_bd_buried;
	const noVat = locationNoVat || !!venue.is_vat_buried;
	const canShowAd = !noSc && (venue.service_charge ?? 0) > 0;
	const canShowBd = !noScBd && (venue.service_charge_bd ?? 0) > 0;
	const defaultType = venue.default_service_charge_type ?? "ad";

	return {
		vat: !noVat && (venue.vat ?? 0) > 0,
		serviceCharge: defaultType === "ad" ? canShowAd : canShowAd && !canShowBd,
		serviceChargeBd: defaultType === "bd" ? canShowBd : canShowBd && !canShowAd,
		// No explicit location-level tax field yet; infer via no_vat.
		tax: !locationNoVat && (venue.tax ?? 0) > 0,
		cardSurcharge: false,
	};
};

export const getCloseTabOrdersAndCustomer = (orders: Order[]) => {
	const unpaidOrders = orders.filter(
		(order) => !order.is_paid && !order.is_cancelled,
	);
	const customers = [
		...new Set(unpaidOrders.map((order) => order.customer).filter(Boolean)),
	];

	return {
		orders: unpaidOrders,
		customer: customers.length === 1 ? customers[0] : undefined,
	};
};

const assertCachedVenue = () => {
	const venue = getCurrentVenueCache();
	if (!venue) {
		toast.error("Venue not found. Try refreshing the page");
		throw new Error("Venue not found");
	}
	return venue;
};

const assertMatchingCachedLocation = (locationId?: string | null) => {
	if (!locationId) {
		toast.error("No location selected");
		throw new Error("No location selected");
	}
	const location = getOperationalLocationsCache().find(
		(l) => l._id === locationId,
	);
	if (!location) {
		toast.error("Location not found. Try refreshing the page");
		throw new Error("Location not found");
	}
	return location;
};

export const useStore = create<State & Actions>()((set, get) => ({
	...INITIAL_STATE,
	resetApp: () => set(INITIAL_STATE),
	setIsReady: (data) => set({ isReady: data }),
	setIsConnectedToServer: (data) => set({ isConnectedToServer: data }),
	setIsFeatureLockedDialogOpen: (data) =>
		set({ isFeatureLockedDialogOpen: data }),
	setToken: (token) => set({ token }),
	setRefreshToken: (refreshToken) => set({ refreshToken }),
	setVenueId: (venueId) => set({ venueId }),
	setStaffToken: (data) => set({ staffToken: data }),
	logout: async () => {
		const { venueId } = get();
		if (!venueId) return;

		const returnUrl =
			`/login/${venueId}` +
			(Router.query.redir ? `?redir=${Router.query.redir}` : "");

		set((prev) => ({
			...INITIAL_STATE,
			denominator: prev.denominator,
			preferredLocation: prev.preferredLocation,
			preferredLocationView: prev.preferredLocationView,
			preferredCalendarView: prev.preferredCalendarView,
			preferredLocationOnTill: prev.preferredLocationOnTill,
			venueId,
			isReady: false,
		}));

		queryClient.removeQueries({
			queryKey: getAuthQueryKey(),
			exact: true,
		});
		await localforage.removeItem(STORAGE_KEYS.TOKEN);
		await localforage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
		await localforage.removeItem(STORAGE_KEYS.LAST_LOGGED_IN_ROLE_ID);
		await localforage.removeItem(STORAGE_KEYS.PREFERRED_LOCATION_ON_TILL);
		await Router.push(returnUrl);
		Router.reload();
		// set({ isReady: true });
	},
	setAppBreadcrumbs: (data) =>
		set((prev) => ({ app: { ...prev.app, breadcrumbs: data } })),
	setReportsSummaryTabFilters: (data) =>
		set((prev) => ({
			reports: {
				...prev.reports,
				summary: { ...prev.reports.summary, ...data },
			},
		})),
	setReportsReceiptsTabFilters: (data) =>
		set((prev) => ({
			reports: {
				...prev.reports,
				receipts: { ...prev.reports.receipts, ...data },
			},
		})),
	setReportsCategoriesTabFilters: (data) =>
		set((prev) => ({
			reports: {
				...prev.reports,
				categories: { ...prev.reports.categories, ...data },
			},
		})),
	setReportsItemsTabFilters: (data) =>
		set((prev) => ({
			reports: {
				...prev.reports,
				items: { ...prev.reports.items, ...data },
			},
		})),
	setReportsOptionsTabFilters: (data) =>
		set((prev) => ({
			reports: {
				...prev.reports,
				options: { ...prev.reports.options, ...data },
			},
		})),
	setReportsCustomsTabFilters: (data) =>
		set((prev) => ({
			reports: {
				...prev.reports,
				customs: { ...prev.reports.customs, ...data },
			},
		})),
	setReportsStaffsTabFilters: (data) =>
		set((prev) => ({
			reports: {
				...prev.reports,
				staffs: { ...prev.reports.staffs, ...data },
			},
		})),
	setReportsLocationsTabFilters: (data) =>
		set((prev) => ({
			reports: {
				...prev.reports,
				locations: { ...prev.reports.locations, ...data },
			},
		})),
	setReportsDetailsTabFilters: (data) =>
		set((prev) => ({
			reports: {
				...prev.reports,
				details: { ...prev.reports.details, ...data },
			},
		})),
	setReportsVoidsTabFilters: (data) =>
		set((prev) => ({
			reports: {
				...prev.reports,
				voids: { ...prev.reports.voids, ...data },
			},
		})),
	resetTill: (data) =>
		set({
			till: { ...INITIAL_STATE.till, sessionId: nextTillSessionId(), ...data },
		}),
	updateTill: (data) => set((prev) => ({ till: { ...prev.till, ...data } })),
	applyTillItemTransition: ({ items, prevItems, cancelledItems }) => {
		const venue = assertCachedVenue();

		set((prev) => {
			const nextTill = { ...prev.till };
			const effectiveAddonsToggles = prev.till.addonsToggles;
			const mode = String(Router.query.mode ?? "");

			const serviceCharge = effectiveAddonsToggles.serviceCharge
				? venue.service_charge
				: 0;
			const serviceChargeBd = effectiveAddonsToggles.serviceChargeBd
				? (venue.service_charge_bd ?? 0)
				: 0;
			const vat = effectiveAddonsToggles.vat ? venue.vat : 0;
			const tax = effectiveAddonsToggles.tax ? (venue.tax ?? 0) : 0;

			if (items !== undefined) {
				const { items: itemsWithVat } = applyVatToOrder({
					order: { items },
					vat,
				});
				const { items: itemsWithServiceCharge } = applyServiceChargeToOrder({
					order: { items: itemsWithVat },
					serviceCharge,
				});
				const { items: itemsWithServiceChargeBd } = applyServiceChargeBdToOrder(
					{
						order: { items: itemsWithServiceCharge } as any,
						serviceChargeBd,
					},
				);
				const { items: itemsWithTax } = applyTaxToOrder({
					order: { items: itemsWithServiceChargeBd },
					tax,
				});
				nextTill.items =
					mode === TILL_MODE.UPDATE_ORDER
						? getUpdateOrderItemInputFromOrderItems(itemsWithTax as OrderItem[])
						: getCreateOrderItemInputFromOrderItems(
								itemsWithTax as OrderItem[],
							);
			}

			if (prevItems !== undefined) {
				const { items: itemsWithVat } = applyVatToOrder({
					order: { items: prevItems as OrderItem[] },
					vat,
				});
				const { items: itemsWithServiceCharge } = applyServiceChargeToOrder({
					order: { items: itemsWithVat },
					serviceCharge,
				});
				const { items: itemsWithServiceChargeBd } = applyServiceChargeBdToOrder(
					{
						order: { items: itemsWithServiceCharge } as any,
						serviceChargeBd,
					},
				);
				const { items: itemsWithTax } = applyTaxToOrder({
					order: { items: itemsWithServiceChargeBd },
					tax,
				});
				nextTill.prevItems = getUpdateOrderItemInputFromOrderItems(
					itemsWithTax as OrderItem[],
				);
			}

			if (cancelledItems !== undefined) {
				const { cancelled_items: cancelledItemsWithVat } = applyVatToOrder({
					order: { cancelled_items: cancelledItems, items: [] },
					vat,
				});
				const { cancelled_items: cancelledItemsWithServiceCharge } =
					applyServiceChargeToOrder({
						order: { cancelled_items: cancelledItemsWithVat, items: [] },
						serviceCharge,
					});
				const { cancelled_items: cancelledItemsWithServiceChargeBd } =
					applyServiceChargeBdToOrder({
						order: {
							cancelled_items: cancelledItemsWithServiceCharge,
							items: [],
						},
						serviceChargeBd,
					});
				const { items: cancelledItemsWithTax } = applyTaxToOrder({
					order: {
						items: cancelledItemsWithServiceChargeBd,
					},
					tax,
				});
				nextTill.cancelled_items = getUpdateOrderItemInputFromOrderItems(
					cancelledItemsWithTax as OrderItem[],
				);
			}

			return { till: nextTill };
		});
	},
	mergeTillCourseIntoPrevious: (lowerCourseIndex) =>
		set((prev) => {
			const items = mergeCourseIntoPrevious(prev.till.items, lowerCourseIndex);
			return {
				till: {
					...prev.till,
					items,
					current_course_index: getNextCourseIndex([
						...prev.till.prevItems,
						...prev.till.cancelled_items,
						...items,
					]),
				},
			};
		}),
	setupTillForSelectedLocation: (data) => {
		const venue = assertCachedVenue();

		const { _id, default_order_note } = data;

		const locationData = assertMatchingCachedLocation(_id);

		const addonsToggles = resolveInitialAddonToggles({
			venue,
			locationNoVat: locationData.no_vat,
			locationNoServiceCharge: locationData.no_service_charge,
		});

		set({
			till: {
				...INITIAL_STATE.till,
				sessionId: nextTillSessionId(),
				hydrationKey: getTillHydrationKey(undefined, _id),
				location: _id,
				note: default_order_note,
				addonsToggles,
			},
		});
	},
	setupTillForOrderUpdate: (data) => {
		const {
			_id,
			items,
			cancelled_items,
			location,
			customer,
			note,
			_receipt,
			is_paid,
			updated_at,
		} = data;
		const venue = assertCachedVenue();
		const currentLocation = assertMatchingCachedLocation(location);

		const addonsToggles = resolveInitialAddonToggles({
			venue,
			locationNoVat: currentLocation.no_vat,
			locationNoServiceCharge: currentLocation.no_service_charge,
		});

		const till = {
			...INITIAL_STATE.till,
			sessionId: nextTillSessionId(),
			hydrationKey: getTillHydrationKey(TILL_MODE.UPDATE_ORDER, _id),
			note,
			location,
			alertContext: { orderId: _id },
			orderEditContext: {
				orderId: _id,
				isPaid: is_paid,
				snapshotUpdatedAt: updated_at,
			},
			items: [], // New items start empty in UPDATE_ORDER mode
			prevItems: getUpdateOrderItemInputFromOrderItems(items), // Existing items go to prevItems
			cancelled_items: getUpdateOrderItemInputFromOrderItems(cancelled_items),
			current_course_index: getNextCourseIndex([
				...items,
				...(cancelled_items ?? []),
			]),
			payment_types: _receipt ? _receipt.payment_types : [],
			customer,
			addonsToggles,
		};
		set({
			till: { ...till, compositionBaseline: createCompositionBaseline(till) },
		});
	},
	setupTillForOrderPayment: (data) => {
		const {
			_id,
			items,
			cancelled_items,
			location,
			_receipt,
			customer,
			note,
			updated_at,
			is_paid,
		} = data;

		const venue = assertCachedVenue();
		const currentLocation = assertMatchingCachedLocation(location);

		const addonsToggles = resolveInitialAddonToggles({
			venue,
			locationNoVat: currentLocation.no_vat,
			locationNoServiceCharge: currentLocation.no_service_charge,
		});

		const till = {
			...INITIAL_STATE.till,
			sessionId: nextTillSessionId(),
			hydrationKey: getTillHydrationKey(TILL_MODE.TAKE_ORDER_PAYMENT, _id),
			note,
			location,
			alertContext: { orderId: _id },
			orderEditContext: {
				orderId: _id,
				isPaid: is_paid,
				snapshotUpdatedAt: updated_at,
			},
			items: [], // New items start empty in TAKE_ORDER_PAYMENT mode
			prevItems: getUpdateOrderItemInputFromOrderItems(items), // Existing items go to prevItems
			cancelled_items: getUpdateOrderItemInputFromOrderItems(cancelled_items),
			current_course_index: getNextCourseIndex([
				...items,
				...(cancelled_items ?? []),
			]),
			payment_types: _receipt ? _receipt.payment_types : [],
			customer,
			isPaymentsPanelOpen: true,
			addonsToggles,
		};
		set({
			till: {
				...till,
				compositionBaseline: createCompositionBaseline(till),
				openedAtMs: Date.now(),
			},
		});
	},
	setupTillForCloseTab: (data) => {
		const { _id, _current_orders } = data;

		const venue = assertCachedVenue();
		const currentLocation = assertMatchingCachedLocation(_id);

		const { orders, customer } = getCloseTabOrdersAndCustomer(_current_orders);
		const addonsToggles = resolveInitialAddonToggles({
			venue,
			locationNoVat: currentLocation.no_vat,
			locationNoServiceCharge: currentLocation.no_service_charge,
		});

		const till = {
			...INITIAL_STATE.till,
			sessionId: nextTillSessionId(),
			hydrationKey: getTillHydrationKey(TILL_MODE.CLOSE_TAB, _id),
			location: _id,
			orders,
			isPaymentsPanelOpen: true,
			customer,
			addonsToggles,
		};
		set({
			till: {
				...till,
				compositionBaseline: createCompositionBaseline(till),
				openedAtMs: Date.now(),
			},
		});
	},
	setTillIsLoading: (data) =>
		set((prev) => ({
			till: { ...prev.till, isLoading: data ?? !prev.till.isLoading },
		})),
	setTillIsPaymentsPanelOpen: (data) =>
		set((prev) => ({ till: { ...prev.till, isPaymentsPanelOpen: data } })),
	setTillSelectedCategory: (data) =>
		set((prev) => ({ till: { ...prev.till, selectedCategory: data } })),
	setTillSelectedItemTags: (data) =>
		set((prev) => ({ till: { ...prev.till, selectedItemTags: data } })),
	setTillSelectedItem: (data) =>
		set((prev) => ({ till: { ...prev.till, selectedItem: data } })),
	setTillSelectedCartItem: (data) =>
		set((prev) => ({ till: { ...prev.till, selectedCartItem: data } })),
	setTillIsCopyingItem: (data) =>
		set((prev) => ({ till: { ...prev.till, isCopyingItem: data } })),
	setTillLocation: (data) => {
		const venue = assertCachedVenue();
		const locationData = assertMatchingCachedLocation(data);

		const { till } = get();
		const { items, orders, addonsToggles } = till;
		const newTill = { ...till };

		const hasPendingItems = items.length > 0 || orders.length > 0;

		let newAddonsToggles: typeof addonsToggles;
		if (hasPendingItems) {
			// Mid-session location change: preserve user choices, apply location restrictions
			newAddonsToggles = {
				...addonsToggles,
				serviceCharge:
					addonsToggles.serviceCharge && !locationData.no_service_charge,
				serviceChargeBd:
					addonsToggles.serviceChargeBd && !locationData.no_service_charge,
				vat: addonsToggles.vat && !locationData.no_vat,
				tax: addonsToggles.tax && !locationData.no_vat,
			};
		} else {
			// Fresh till (initial load or no items): initialize from venue defaults
			newAddonsToggles = resolveInitialAddonToggles({
				venue,
				locationNoVat: locationData.no_vat,
				locationNoServiceCharge: locationData.no_service_charge,
			});
		}

		const serviceCharge = newAddonsToggles.serviceCharge
			? venue.service_charge
			: 0;
		const serviceChargeBd = newAddonsToggles.serviceChargeBd
			? (venue.service_charge_bd ?? 0)
			: 0;
		const vat = newAddonsToggles.vat ? venue.vat : 0;

		newTill.location = data;
		newTill.addonsToggles = newAddonsToggles;

		if (items.length) {
			const { items: itemsWithVat } = applyVatToOrder({
				order: { items: items },
				vat,
			});
			const { items: itemsWithServiceCharge } = applyServiceChargeToOrder({
				order: { items: itemsWithVat },
				serviceCharge,
			});
			const { items: itemsWithServiceChargeBd } = applyServiceChargeBdToOrder({
				order: { items: itemsWithServiceCharge } as any,
				serviceChargeBd,
			});
			const { items: itemsWithTax } = applyTaxToOrder({
				order: { items: itemsWithServiceChargeBd },
				tax: newAddonsToggles.tax ? (venue.tax ?? 0) : 0,
			});
			newTill.items = itemsWithTax as (typeof newTill)["items"];
		}

		if (orders.length) {
			const ordersWithVat = applyVatToOrderGroup({ orders, vat });
			const ordersWithServiceCharge = applyServiceChargeToOrderGroup({
				orders: ordersWithVat,
				serviceCharge,
			});
			const ordersWithServiceChargeBd = applyServiceChargeBdToOrderGroup({
				orders: ordersWithServiceCharge as any,
				serviceChargeBd,
			});
			const ordersWithTax = applyTaxToOrderGroup({
				// @ts-ignore
				orders: ordersWithServiceChargeBd,
				tax: newAddonsToggles.tax ? (venue.tax ?? 0) : 0,
			});

			newTill.orders = ordersWithTax as unknown as (typeof newTill)["orders"];
		}

		set({ till: newTill });
	},
	setDenominator: (data) => {
		set({ denominator: data });
		localforage.setItem(STORAGE_KEYS.DENOMINATOR, data).catch(console.error);
	},
	setPreferredLocation: (data) => {
		set({ preferredLocation: data });
		localforage
			.setItem(STORAGE_KEYS.PREFERRED_LOCATION, data)
			.catch(console.error);
	},
	setPreferredLocationView: (data) => {
		set({ preferredLocationView: data });
		localforage
			.setItem(STORAGE_KEYS.PREFERRED_LOCATION_VIEW, data)
			.catch(console.error);
	},
	setPreferredCalendarView: (data) => {
		set({ preferredCalendarView: data });
		localforage
			.setItem(STORAGE_KEYS.PREFERRED_CALENDAR_VIEW, data)
			.catch(console.error);
	},
	setPreferredLocationOnTill: (data) => {
		set({ preferredLocationOnTill: data });
		localforage
			.setItem(STORAGE_KEYS.PREFERRED_LOCATION_ON_TILL, data)
			.catch(console.error);
	},
	updateOrdersLogFilters: (data) => {
		set((prev) => ({
			ordersLog: {
				...prev.ordersLog,
				filters: {
					...prev.ordersLog.filters,
					...data,
				},
			},
		}));
	},
	updateCustomersFilters: (data) => {
		set((prev) => ({
			customers: {
				...prev.customers,
				filters: {
					...prev.customers.filters,
					...data,
				},
			},
		}));
	},
	updatePrintLogsFilters: (data) => {
		set((prev) => ({
			printLogs: {
				...prev.printLogs,
				filters: {
					...prev.printLogs.filters,
					...data,
				},
			},
		}));
	},
	updateRawMaterialReceivedFilters: (data) => {
		set((prev) => ({
			rawMaterialReceived: {
				...prev.rawMaterialReceived,
				filters: {
					...prev.rawMaterialReceived.filters,
					...data,
				},
			},
		}));
	},
	setRoomMasterError: (data) => set({ roomMasterError: data }),
	setLastOfflineDataDownloadAt: (data) =>
		set({ lastOfflineDataDownloadAt: data }),
	setIsDownloadingOfflineData: (data) =>
		set({ isDownloadingOfflineData: data }),
}));

const { use, setState, getState } = createSelectors(useStore);

const useIsReady = () => {
	return use.isReady();
};
const useSetIsReady = () => {
	return use.setIsReady();
};
const useIsConnectedToServer = () => {
	return use.isConnectedToServer();
};
const useSetIsConnectedToServer = () => {
	return use.setIsConnectedToServer();
};
const useIsFeatureLockedDialogOpen = () => {
	return use.isFeatureLockedDialogOpen();
};
const useSetIsFeatureLockedDialogOpen = () => {
	return use.setIsFeatureLockedDialogOpen();
};
const useToken = () => {
	return use.token();
};
const useSetToken = () => {
	return use.setToken();
};
const useVenueId = () => {
	return use.venueId();
};
const useSetVenueId = () => {
	return use.setVenueId();
};
const useCart = () => {
	return use.till();
};
const useResetCart = () => {
	return use.resetTill();
};
const useLogout = () => {
	return use.logout();
};
const useAppBreadcrumbs = () => {
	return useStore((s) => s.app?.breadcrumbs);
};
const useSetAppBreadcrumbs = () => {
	return use.setAppBreadcrumbs();
};
const useReportsSummaryTabFilters = () => {
	return useStore((store) => store.reports.summary);
};
const useSetReportsSummaryTabFilters = () => {
	return use.setReportsSummaryTabFilters();
};
const useReportsReceiptsTabFilters = () => {
	return useStore((store) => store.reports.receipts);
};
const useSetReportsReceiptsTabFilters = () => {
	return use.setReportsReceiptsTabFilters();
};
const useReportsCategoriesTabFilters = () => {
	return useStore((store) => store.reports.categories);
};
const useSetReportsCategoriesTabFilters = () => {
	return use.setReportsCategoriesTabFilters();
};
const useReportsItemsTabFilters = () => {
	return useStore((store) => store.reports.items);
};
const useSetReportsItemsTabFilters = () => {
	return use.setReportsItemsTabFilters();
};
const useReportsOptionsTabFilters = () => {
	return useStore((store) => store.reports.options);
};
const useSetReportsOptionsTabFilters = () => {
	return use.setReportsOptionsTabFilters();
};
const useSetReportsCustomsTabFilters = () => {
	return use.setReportsCustomsTabFilters();
};
const useReportsStaffsTabFilters = () => {
	return useStore((store) => store.reports.staffs);
};
const useSetReportsStaffsTabFilters = () => {
	return use.setReportsStaffsTabFilters();
};
const useReportsLocationsTabFilters = () => {
	return useStore((store) => store.reports.locations);
};
const useSetReportsLocationsTabFilters = () => {
	return use.setReportsLocationsTabFilters();
};
const useReportsDetailsTabFilters = () => {
	return useStore((store) => store.reports.details);
};
const useSetReportsDetailsTabFilters = () => {
	return use.setReportsDetailsTabFilters();
};
const useReportsVoidsTabFilters = () => {
	return useStore((store) => store.reports.voids);
};
const useSetReportsVoidsTabFilters = () => {
	return use.setReportsVoidsTabFilters();
};
const useTillOrders = () => {
	return useStore((store) => store.till.orders);
};
const useTillItems = () => {
	return useStore((store) => store.till.items);
};
const useTillCancelledItems = () => {
	return useStore((store) => store.till.cancelled_items);
};
const useTillPaymentTypes = () => {
	return useStore((store) => store.till.payment_types);
};
const useTillLocation = () => {
	return useStore((store) => store.till.location);
};
const useTillOrderEditContext = () => {
	return useStore((store) => store.till.orderEditContext);
};
const useTillHydrationKey = () => {
	return useStore((store) => store.till.hydrationKey);
};
const useTillCustomer = () => {
	return useStore((store) => store.till.customer);
};
const useTillPaymentsReceived = () => {
	return useStore((store) => store.till.cash_received);
};
const useTillPaymentsReturned = () => {
	return useStore((store) => store.till.cash_returned);
};
const useTillNote = () => {
	return useStore((store) => store.till.note);
};
const useResetTill = () => {
	return use.resetTill();
};
const useUpdateTill = () => {
	return use.updateTill();
};
const useSetupTillForSelectedLocation = () => {
	return use.setupTillForSelectedLocation();
};
const useSetupTillForOrderUpdate = () => {
	return use.setupTillForOrderUpdate();
};
const useSetupTillForOrderPayment = () => {
	return use.setupTillForOrderPayment();
};
const useSetupTillForCloseTab = () => {
	return use.setupTillForCloseTab();
};
const useTillIsInternalReceipt = () => {
	return useStore((store) => store.till.isInternalReceipt);
};
const useTillIsLoading = () => {
	return useStore((store) => store.till.isLoading);
};
const useTillIsPaymentsPanelOpen = () => {
	return useStore((store) => store.till.isPaymentsPanelOpen);
};
const useTillSelectedCategory = () => {
	return useStore((store) => store.till.selectedCategory);
};
const useTillSelectedItemTags = () => {
	return useStore((store) => store.till.selectedItemTags);
};
const useSetTillSelectedItemTags = () => {
	return use.setTillSelectedItemTags();
};
const useTillSelectedItem = () => {
	return useStore((store) => store.till.selectedItem);
};
const useTillPrevItems = () => {
	return useStore((store) => store.till.prevItems);
};
const useSetTillIsLoading = () => {
	return use.setTillIsLoading();
};
const useSetTillIsPaymentsPanelOpen = () => {
	return use.setTillIsPaymentsPanelOpen();
};
const useSetTillSelectedCategory = () => {
	return use.setTillSelectedCategory();
};
const useSetTillSelectedItem = () => {
	return use.setTillSelectedItem();
};
const useTillSelectedCartItem = () => {
	return useStore((store) => store.till.selectedCartItem);
};
const useSetTillSelectedCartItem = () => {
	return use.setTillSelectedCartItem();
};
const useTillCloudbedsPayment = () => {
	return useStore((store) => store.till.cloudbeds_payment);
};
const useTillIsCopyingItem = () => {
	return useStore((store) => store.till.isCopyingItem);
};
const useSetTillIsCopyingItem = () => {
	return use.setTillIsCopyingItem();
};
const useSetTillLocation = () => {
	return use.setTillLocation();
};
const useApplyTillItemTransition = () => {
	return use.applyTillItemTransition();
};
const useTillRoomMasterFolio = () => {
	return useStore((store) => store.till.room_master_folio);
};
const useTillRoomMasterRef = () => {
	return useStore((store) => store.till.room_master_reference);
};
const useTillRoomNumber = () => {
	return useStore((store) => store.till.room_number);
};
const useTillTips = () => {
	return useStore((store) => store.till.tips);
};
const useTillCurrentCourseIndex = () => {
	return useStore((store) => store.till.current_course_index);
};
const useMergeTillCourseIntoPrevious = () => {
	return use.mergeTillCourseIntoPrevious();
};
const useTillActiveMasterView = () => {
	return useStore((store) => store.till.activeMasterView);
};
const useTillMasterItemFilter = () => {
	const selectedCategory = useTillSelectedCategory();
	return useStore((store) => {
		if (!selectedCategory?._id) return null;
		return store.till.masterItemFilterByCategory[selectedCategory._id] ?? null;
	});
};
const useDenominator = () => {
	return useStore((store) => store.denominator);
};
const usePreferredLocation = () => {
	return useStore((store) => store.preferredLocation);
};
const usePreferredLocationView = () => {
	return useStore((store) => store.preferredLocationView);
};
const usePreferredLocationOnTill = () => {
	return useStore((store) => store.preferredLocationOnTill);
};
const usePreferredCalendarView = () => {
	return useStore((store) => store.preferredCalendarView);
};
const useSetDenominator = () => {
	return use.setDenominator();
};
const useSetPreferredLocation = () => {
	return use.setPreferredLocation();
};
const useSetPreferredLocationView = () => {
	return use.setPreferredLocationView();
};
const useSetPreferredLocationOnTill = () => {
	return use.setPreferredLocationOnTill();
};
const useSetPreferredCalendarView = () => {
	return use.setPreferredCalendarView();
};

const getCreateOrderItemInputFromOrderItems = (items: OrderItem[]) =>
	items.map((i) => ({
		_id: i._id,
		category: i.category,
		item: i.item,
		is_master: i.is_master,
		master_parent: i.master_parent,
		unit_price: i.unit_price,
		options: i.options,
		quantity: i.quantity,
		comments: i.comments,
		cancelled_reason: i.cancelled_reason,
		subtotal: i.subtotal,
		subtotal_addons: i.subtotal_addons,
		net_amount: i.net_amount,
		gross_amount: i.gross_amount,
		cancelled_amount: i.cancelled_amount,
		item_id: i.item_id,
		cost: i.cost,
		title: i.title,
		original_price: i.original_price,
		minimum_required_price: i.minimum_required_price,
		original_price_addons: i.original_price_addons,
		listed_price: i.listed_price,
		extra_quantity: i.extra_quantity,
		serving_quantity: i.serving_quantity,
		type: i.type,
		course_index: i.course_index,
		prep_time: i.prep_time,
		printer_tag: i.printer_tag,
		recipe: i.recipe,
		no_vat: i.no_vat,
		no_service_charge: i.no_service_charge,
		is_open_item: i.is_open_item,
		is_hide_from_receipt: i.is_hide_from_receipt,
	}));

const getUpdateOrderItemInputFromOrderItems = (
	items: OrderItem[],
): UpdateOrderItemInput[] =>
	items.map((i) => ({
		_id: i._id,
		category: i.category,
		item: i.item,
		is_master: i.is_master,
		master_parent: i.master_parent,
		item_id: i.item_id,
		cost: i.cost,
		unit_price: i.unit_price,
		options: i.options,
		quantity: i.quantity,
		comments: i.comments,
		cancelled_reason: i.cancelled_reason,
		subtotal: i.subtotal,
		subtotal_addons: i.subtotal_addons,
		net_amount: i.net_amount,
		gross_amount: i.gross_amount,
		cancelled_amount: i.cancelled_amount,
		title: i.title,
		original_price: i.original_price,
		minimum_required_price: i.minimum_required_price,
		original_price_addons: i.original_price_addons,
		listed_price: i.listed_price,
		extra_quantity: i.extra_quantity,
		serving_quantity: i.serving_quantity,
		type: i.type,
		course_index: i.course_index,
		prep_time: i.prep_time,
		printer_tag: i.printer_tag,
		delivered_count: i.delivered_count,
		status: i.status,
		recipe: i.recipe,
		no_vat: i.no_vat,
		no_service_charge: i.no_service_charge,
		is_open_item: i.is_open_item,
		is_hide_from_receipt: i.is_hide_from_receipt,
		is_paid: i.is_paid,
		created_at: i.created_at,
		updated_at: i.updated_at,
		non_sales_credit_amount: i.non_sales_credit_amount,
		inventory_deduction_category: (i as UpdateOrderItemInput)
			.inventory_deduction_category,
	}));

export {
	getCreateOrderItemInputFromOrderItems,
	getState,
	getUpdateOrderItemInputFromOrderItems,
	setState,
	useAppBreadcrumbs,
	useCart,
	useDenominator,
	useIsConnectedToServer,
	useIsFeatureLockedDialogOpen,
	useIsReady,
	useLogout,
	usePreferredCalendarView,
	usePreferredLocation,
	usePreferredLocationOnTill,
	usePreferredLocationView,
	useReportsCategoriesTabFilters,
	useReportsDetailsTabFilters,
	useReportsItemsTabFilters,
	useReportsLocationsTabFilters,
	useReportsOptionsTabFilters,
	useReportsReceiptsTabFilters,
	useReportsStaffsTabFilters,
	useReportsSummaryTabFilters,
	useReportsVoidsTabFilters,
	useResetCart,
	useResetTill,
	useSetAppBreadcrumbs,
	useSetDenominator,
	useSetIsConnectedToServer,
	useSetIsFeatureLockedDialogOpen,
	useSetIsReady,
	useSetPreferredCalendarView,
	useSetPreferredLocation,
	useSetPreferredLocationOnTill,
	useSetPreferredLocationView,
	useSetReportsCategoriesTabFilters,
	useSetReportsCustomsTabFilters,
	useSetReportsDetailsTabFilters,
	useSetReportsItemsTabFilters,
	useSetReportsLocationsTabFilters,
	useSetReportsOptionsTabFilters,
	useSetReportsReceiptsTabFilters,
	useSetReportsStaffsTabFilters,
	useSetReportsSummaryTabFilters,
	useSetReportsVoidsTabFilters,
	useSetTillIsLoading,
	useSetTillIsPaymentsPanelOpen,
	useSetTillLocation,
	useApplyTillItemTransition,
	useSetTillSelectedCartItem,
	useSetTillSelectedCategory,
	useSetTillSelectedItemTags,
	useSetTillSelectedItem,
	useSetTillIsCopyingItem,
	useSetToken,
	useSetupTillForOrderUpdate,
	useSetupTillForCloseTab,
	useSetupTillForOrderPayment,
	useSetupTillForSelectedLocation,
	useSetVenueId,
	useTillCancelledItems,
	useTillCustomer,
	useTillIsInternalReceipt,
	useTillIsLoading,
	useTillIsPaymentsPanelOpen,
	useTillItems,
	useTillLocation,
	useTillNote,
	useTillOrderEditContext,
	useTillHydrationKey,
	useTillOrders,
	useTillPaymentsReceived,
	useTillPaymentsReturned,
	useTillPaymentTypes,
	useTillPrevItems,
	useTillRoomMasterFolio,
	useTillRoomMasterRef,
	useTillRoomNumber,
	useTillSelectedCartItem,
	useTillSelectedCategory,
	useTillSelectedItemTags,
	useTillSelectedItem,
	useTillCloudbedsPayment,
	useTillIsCopyingItem,
	useTillCurrentCourseIndex,
	useMergeTillCourseIntoPrevious,
	useTillTips,
	useTillActiveMasterView,
	useTillMasterItemFilter,
	useToken,
	useUpdateTill,
	useVenueId,
};

export const useOrdersLogFilters = () => {
	return useStore((store) => store.ordersLog.filters);
};
export const useUpdateOrdersLogFilters = () => {
	return use.updateOrdersLogFilters();
};
export const useCustomersFilters = () => {
	return useStore((store) => store.customers.filters);
};
export const useUpdateCustomersFilters = () => {
	return use.updateCustomersFilters();
};
export const usePrintLogsFilters = () => {
	return useStore((store) => store.printLogs.filters);
};
export const useUpdatePrintLogsFilters = () => {
	return use.updatePrintLogsFilters();
};
export const useRawMaterialReceivedFilters = () => {
	return useStore((store) => store.rawMaterialReceived.filters);
};
export const useUpdateRawMaterialReceivedFilters = () => {
	return use.updateRawMaterialReceivedFilters();
};
